/**
 * Model-picker selection state.
 *
 * Three surfaces let a user pick an LLM model from a dropdown that always ends
 * with "Other / Custom Model Name...": the BYOK panel in the generate modal,
 * the BYOK settings panel, and the super-admin hosted-provider form.
 *
 * The custom entry is the one option with no model identifier behind it, so it
 * cannot be represented by the model string alone. Deriving the dropdown's value
 * from the model string made that state unrepresentable: choosing the custom
 * entry cleared the model, the derivation read the cleared model as "nothing
 * chosen yet", and the dropdown snapped back to the first listed model. The
 * custom text input, gated on the dropdown reading `__custom__`, could then
 * never be reached.
 *
 * The fix carries the choice explicitly alongside the value, so an empty model
 * string can mean "custom, not typed yet" instead of "unset".
 */

export const CUSTOM_MODEL_OPTION = '__custom__';

/**
 * The dropdown's value.
 *
 * An explicit custom choice wins over the model string, which is what keeps the
 * custom input reachable while it is still empty. Otherwise a listed model shows
 * itself; a non-empty but unlisted model is what a saved custom identifier looks
 * like, so it maps to the custom entry; an unset model falls back to the first
 * option, preserving the original default.
 */
export function resolveModelSelectValue(
  models: readonly { value: string }[],
  model: string,
  isCustom: boolean,
): string {
  if (isCustom) return CUSTOM_MODEL_OPTION;
  if (models.some((m) => m.value === model)) return model;
  if (model) return CUSTOM_MODEL_OPTION;
  return models[0]?.value ?? '';
}

/** The state change for a dropdown selection: either a listed model or custom. */
export function modelSelection(value: string): { isCustom: boolean; model: string } {
  return value === CUSTOM_MODEL_OPTION
    ? { isCustom: true, model: '' }
    : { isCustom: false, model: value };
}
